// eslint-disable-next-line jira/restricted/react
import React, { type ForwardedRef, PureComponent, type ComponentType, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import omit from 'lodash/omit';
import { createRaf } from '../../../../../utils';
import type { Position, OnMouseDown, OnClick, OnDragStart, OnDrag, OnDragEnd } from './types';

const DEFAULT_DRAG_THRESHOLD = 3;

const DISABLED_DRAG_PROPS = ['onMouseDown', 'onDragStart', 'onDrag', 'onDragEnd'];
const ENABLED_DRAG_PROPS = ['onMouseDown', 'onClick', 'onDragStart', 'onDrag', 'onDragEnd'];

const getMousePosition = (event: MouseEvent) => ({
	x: event.pageX,
	y: event.pageY,
});

const requestAnimationFrame = createRaf();

type Props = {
	isDisabled: boolean;
	// Immediately when the mouse was pressed down
	onMouseDown: OnMouseDown | undefined;
	// Called when the mouse was pressed, not moved beyond the drag threshold, then released
	onClick: OnClick | undefined;
	// Called when the mouse was pressed down, then moved beyond the drag threshold.
	// Is provided the (page relative) position the drag was started from.
	onDragStart: OnDragStart | undefined;
	// Called when dragging.
	// Is provided the from, to and start positions
	onDrag: OnDrag | undefined;
	// Called when dragging and the mouse was released
	// Is provided the start and end positions of the entire drag
	onDragEnd: OnDragEnd | undefined;

	forwardedRef: ForwardedRef<HTMLElement>;
};

type State = {
	dragStart?: Position;
	previousDragPosition?: Position;
	isDragging: boolean;
};

const withDragObserver = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	WrappedComponent: ComponentType<any>,
	threshold: number = DEFAULT_DRAG_THRESHOLD,
) => {
	// eslint-disable-next-line jira/react/no-class-components
	class DragObserver extends PureComponent<Props, State> {
		static defaultProps = {
			isDisabled: false,
			onMouseDown: undefined,
			onDragStart: undefined,
			onDrag: undefined,
			onDragEnd: undefined,
			onClick: undefined,
		};

		constructor(props: Props) {
			super(props);

			this.mounted = false;
			this.state = {
				isDragging: false,
			};
		}

		componentDidMount() {
			this.mounted = true;
			// eslint-disable-next-line react/no-find-dom-node
			this.ref = ReactDOM.findDOMNode(this);

			if (this.ref) {
				// @ts-expect-error - TS2345 - Argument of type '(event: MouseEvent) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
				this.ref.addEventListener('mousedown', this.onChildMouseDown);
			}
		}

		componentWillUnmount() {
			if (this.ref) {
				// @ts-expect-error - TS2345 - Argument of type '(event: MouseEvent) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
				this.ref.removeEventListener('mousedown', this.onChildMouseDown);
			}
			this.mounted = false;
		}

		onChildMouseDown = (event: MouseEvent) => {
			if (this.props.isDisabled) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			window.addEventListener('mousemove', this.onWindowMouseMove);
			window.addEventListener('mouseup', this.onWindowMouseUp);

			const dragStart = getMousePosition(event);

			this.setState({
				isDragging: false,
				dragStart,
				previousDragPosition: dragStart,
			});

			this.onMouseDown(event);
		};

		onWindowMouseMove = (event: MouseEvent) => {
			event.preventDefault();

			const mouse = getMousePosition(event);
			const { isDragging, dragStart, previousDragPosition } = this.state;

			if (!dragStart || !previousDragPosition) {
				throw Error(
					'Assertion failed: dragStart or previousDragPosition are not defined. Each mouseMove should only be called after an initial mouseDown where these values were set.',
				);
			}

			if (!isDragging) {
				if (
					Math.abs(mouse.x - dragStart.x) > threshold ||
					Math.abs(mouse.y - dragStart.y) > threshold
				) {
					this.setState({ isDragging: true });
					this.onDragStart(dragStart);
				}
			} else {
				const from = previousDragPosition;
				const to = mouse;

				this.setState({ previousDragPosition: mouse });

				this.onDrag(from, to, dragStart);
			}
		};

		onWindowMouseUp = (event: MouseEvent) => {
			const { isDragging, dragStart } = this.state;

			if (!dragStart) {
				throw Error(
					'Assertion failed: dragStart is not defined. Each mouseUp should be paired with a mouseDown where dragStart was set.',
				);
			}

			if (isDragging) {
				const mouse = getMousePosition(event);
				const start = dragStart;
				const end = mouse;

				if (this.mounted) {
					this.setState({ isDragging: false });
				}
				this.onDragEnd(start, end);
			} else {
				this.onClick(event);
			}

			window.removeEventListener('mousemove', this.onWindowMouseMove);
			window.removeEventListener('mouseup', this.onWindowMouseUp);
		};

		onMouseDown(event: MouseEvent) {
			if (this.props.onMouseDown) {
				this.props.onMouseDown(event);
			}
		}

		onDragStart(from: Position) {
			if (this.props.onDragStart) {
				this.props.onDragStart(from);
			}
		}

		onDrag(from: Position, to: Position, start: Position) {
			if (this.props.onDrag) {
				requestAnimationFrame(() => {
					// Flow thinks we haven't checked for defined of onDrag due to the new function scope
					// @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
					this.props.onDrag(from, to, start);
				});
			}
		}

		onDragEnd(from: Position, to: Position) {
			if (this.props.onDragEnd) {
				this.props.onDragEnd(from, to);
			}
		}

		onClick(event: MouseEvent) {
			if (this.props.onClick) {
				this.props.onClick(event);
			}
		}

		/* isMounted is a class method and thus reserved */
		mounted: boolean;

		// @ts-expect-error - TS2564 - Property 'ref' has no initializer and is not definitely assigned in the constructor.
		ref: Element | Text | null;

		render() {
			const { isDisabled, forwardedRef } = this.props;
			const { isDragging } = this.state;

			/* When dragging is disabled, clicking should still be enabled.
			 * Rather than try handle onClick in this HOC, we instead just allow
			 * the onClick passed in to make it to the underlying component.
			 */
			const safeProps = omit(this.props, isDisabled ? DISABLED_DRAG_PROPS : ENABLED_DRAG_PROPS);

			return <WrappedComponent isDragging={isDragging} {...safeProps} ref={forwardedRef} />;
		}
	}

	return forwardRef<HTMLElement, Omit<Props, 'forwardedRef'>>((props, ref) => (
		<DragObserver {...props} forwardedRef={ref} />
	));
};

export default withDragObserver;
export type { Position };
